import React, { useState } from 'react';
import { Avatar, Button, Card, Descriptions, Empty, PageHeader, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import { RobotOutlined, SmileOutlined, PhoneOutlined, MailOutlined, CheckCircleOutlined, SafetyOutlined, BranchesOutlined } from '@ant-design/icons';
import moment from 'moment';
import firebase from 'firebase/app';

import CenteredSpinner from '../../../components/CenteredSpinner';
import { RestoreCredentialSection } from '../../../components/admin/RestoreCredentialSection';
import { useUser } from '../../../models/useUser';
import { useClient } from '../../../models/useClient';
import { useCustomerByUserId } from '../../../models/useCustomerByUserId';
import { useClientForOwner } from '../../../models/useClientForOwner';
import { useIsStrikeAdmin } from '../../../models/useIsStrikeAdmin';
import { useTag } from '../../../models/useTag';
import { getAppBaseUrl } from '../../../utils/URLBuilder';
import { decodeOriginParams } from '../../../utils/loginOrigin';
import { findUserInconsistencies } from '../../../utils/userConsistency';

import '../../../App.scss';
import 'antd/dist/antd.css';

const { Text } = Typography;
const isLocalHost = window.location.hostname === 'localhost';
const URLPath = getAppBaseUrl();


function formatLoginMethod(authProvider) {
  const normalized = authProvider ? `${authProvider}`.toLowerCase() : null;
  if (!normalized) return 'Unknown';
  if (normalized === 'google') return 'Google';
  if (normalized === 'apple') return 'Apple';
  if (normalized === 'email') return 'Email';
  if (normalized === 'phone') return 'Phone';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function getAllAuthProviders(user, linkedClient) {
  const seen = new Set();
  const add = (v) => { if (v) seen.add(`${v}`.toLowerCase()); };
  if (user) {
    add(user.authProvider);
    (user.authProviders || []).forEach(add);
  }
  if (linkedClient) {
    add(linkedClient.authProvider);
    (linkedClient.authProviders || []).forEach(add);
  }
  return [...seen];
}

// Moved to utils/userConsistency so the Users LIST can run the doc-only subset
// per row; re-exported here so existing imports (and the findings tests) hold.
export { findUserInconsistencies };

function getOriginContext(user, linkedClient) {
  const signupReason = (user && user.signupOriginReason) || null;
  const signupPath = (user && user.signupOriginPath) || null;
  const signupParams = (user && user.signupOriginParams) || null;
  const reason = signupReason
    || (user && user.originReason)
    || (user && user.lastOriginReason)
    || (linkedClient && linkedClient.originReason)
    || null;
  const path = signupPath
    || (user && user.originPath)
    || (user && user.lastOriginPath)
    || (linkedClient && linkedClient.originPath)
    || null;
  const params = signupParams
    || (user && user.originParams)
    || (user && user.lastOriginParams)
    || (linkedClient && linkedClient.originParams)
    || null;
  return { reason, path, params };
}

/** Normalize paths to canonical shareable format for admin display. */
function toCanonicalDisplayPath(path, reason) {
  if (!path || typeof path !== 'string') return path;
  // Claim tag: show /tags/:tagId (customer-facing page), not /client/claim/:tagId
  if (reason === 'claim_tag') {
    const claimMatch = path.match(/\/client\/claim\/([^/?#]+)/);
    if (claimMatch) return `/tags/${claimMatch[1]}`;
    const tagsMatch = path.match(/\/tags\/([^/?#]+)/);
    if (tagsMatch) return path; // already correct
  }
  // Team invite: show /client/manage/account/teamInvites/...
  const match = path.match(/\/client\/manage\/(?:[^/]+)\/teamInvites\/([^/]+)\/([^/?#]+)/);
  if (match) return `/client/manage/account/teamInvites/${match[1]}/${match[2]}`;
  const matchNoClient = path.match(/\/client\/manage\/teamInvites\/([^/]+)\/([^/?#]+)/);
  if (matchNoClient) return `/client/manage/account/teamInvites/${matchNoClient[1]}/${matchNoClient[2]}`;
  return path;
}

/** Extract tagId from path or params for claim_tag. */
function getTagIdFromOrigin(path, params) {
  if (path) {
    const m = path.match(/\/(?:client\/claim|tags)\/([^/?#]+)/);
    if (m) return m[1];
  }
  const p = decodeOriginParams(params);
  return p.tagId || null;
}

export default function AdminUserScreen(props) {
  const userId = props && props.match && props.match.params ? props.match.params.userId : null;
  const [loadingUser, user] = useUser(userId);
  const [loadingStampedClient, stampedClient] = useClient(user?.clientId ?? null);
  // Pre-attribution veterans own a client but their users doc has no clientId stamp —
  // resolve by ownerId (the same query the login screen trusts) so they don't render
  // as "No linked client". Only queried when the stamp is absent.
  const ownerFallbackId = user && !user.clientId ? userId : null;
  const [loadingOwnedClient, ownedClient] = useClientForOwner(ownerFallbackId);
  const linkedClient = stampedClient || ownedClient || null;
  const linkedByOwnerOnly = !!(!stampedClient && ownedClient);
  const loadingClient = user?.clientId ? loadingStampedClient : loadingOwnedClient;
  const [, customer] = useCustomerByUserId(userId);
  const [, isStrikeAdmin] = useIsStrikeAdmin(userId);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [savingHuman, setSavingHuman] = useState(false);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [adminError, setAdminError] = useState(null);

  const updateEmailVerified = async (nextValue) => {
    if (!userId) return;
    setSaving(true);
    try {
      const setUserEmailVerifiedOverride = firebase
        .functions()
        .httpsCallable('userService-humanHandler-setUserEmailVerifiedOverride');
      await setUserEmailVerifiedOverride({
        userId,
        emailVerified: !!nextValue,
      });
    } catch (error) {
      console.error('[AdminUserScreen] Failed to update emailVerified:', error);
    } finally {
      setSaving(false);
    }
  };

  const resendVerificationEmail = async () => {
    if (!userId) return;
    setResending(true);
    setResendError(null);
    setResendSuccess(false);
    try {
      const resendEmailVerification = firebase
        .functions()
        .httpsCallable('userService-humanHandler-resendEmailVerification');
      const continueUrl = getAppBaseUrl();
      const result = await resendEmailVerification({ userId, continueUrl });
      if (result.data && result.data.verificationLink) {
        console.log('%c[EMULATOR] Verification link:', 'color: #ff0098; font-weight: bold;', result.data.verificationLink);
      }
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (error) {
      console.error('[AdminUserScreen] Failed to resend verification email:', error);
      setResendError(error.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const adminSetHuman = async (nextValue) => {
    if (!userId) return;
    setSavingHuman(true);
    try {
      const fn = firebase.functions().httpsCallable('userService-humanHandler-adminSetHuman');
      await fn({ userId, isHuman: !!nextValue });
    } catch (error) {
      console.error('[AdminUserScreen] Failed to set isHuman:', error);
    } finally {
      setSavingHuman(false);
    }
  };

  const toggleAdmin = async (makeAdmin) => {
    if (!userId) return;
    setSavingAdmin(true);
    setAdminError(null);
    try {
      const ref = firebase
        .firestore()
        .collection('clients')
        .doc('strike')
        .collection('admins')
        .doc(userId);
      if (makeAdmin) {
        await ref.set({ role: 'Admin' });
      } else {
        await ref.delete();
      }
    } catch (err) {
      console.error('[AdminUserScreen] Failed to toggle admin:', err);
      setAdminError(err.message || 'Failed to update admin status');
    } finally {
      setSavingAdmin(false);
    }
  };

  const originContext = getOriginContext(user, linkedClient);
  const originPath = originContext.path || null;
  const originReason = originContext.reason || null;
  const originParams = originContext.params || null;
  const claimTagId = originReason === 'claim_tag' ? getTagIdFromOrigin(originPath, originParams) : null;
  const [tagLoading, tag] = useTag(claimTagId);
  const templateTagId = tag?.templateTagId || null;

  const displayPath = toCanonicalDisplayPath(originPath, originReason) || originPath;
  const originPathLink = displayPath && displayPath.startsWith('/')
    ? `${URLPath}${displayPath}`
    : displayPath;

  if (loadingUser) {
    return <CenteredSpinner loading={true} noLayout={false} />;
  }

  if (!user) {
    return (
      <div style={{ height: '100%', textAlign: 'center' }}>
        <PageHeader ghost={false} title="User Not Found" onBack={() => window.history.back()} />
        <Empty />
      </div>
    );
  }

  return (
    <div style={{ height: '100%' }}>
      <PageHeader
        ghost={false}
        title={`User: ${user.displayName || user.email || user.id}`}
        subTitle={user.id}
        onBack={() => window.history.back()}
      />

      <div style={{ padding: 12 }}>
        {(() => {
          // Hold the banner until the linked client has actually loaded — a slow
          // client read must not flash "client doc missing" (never conflate loading
          // with absent; that ambiguity caused the admin forced-logout confusion).
          if (loadingClient) return null;
          const inconsistencies = findUserInconsistencies(user, linkedClient);
          if (!inconsistencies.length) return null;
          return (
            <Card
              style={{ marginBottom: 12, borderColor: '#ff4d4f', background: '#fff2f0' }}
              data-testid="user-inconsistency-banner"
            >
              <Space direction="vertical" size={2}>
                <Text strong type="danger">⚠ INCONSISTENT — needs review</Text>
                {inconsistencies.map((finding) => (
                  <Text key={finding} type="danger" style={{ fontSize: 13 }}>• {finding}</Text>
                ))}
              </Space>
            </Card>
          );
        })()}
        {/* Same-uid credential restore — only offered where a tombstone says the
            auth user is gone. The section previews every existing credential for
            the uid/email before allowing the (server-confirmed) restore. */}
        {user.deleted === true && <RestoreCredentialSection uid={user.id} />}
        <Card>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Avatar size={64} src={user.photoURL || null}>
                {(user.displayName || user.email || 'U').charAt(0)}
              </Avatar>
              <Space direction="vertical" size={0}>
                <Text strong>{user.displayName || 'No display name'}</Text>
                {user.email ? (
                  <Text type="secondary"><MailOutlined style={{ marginRight: 4 }} />{user.email}</Text>
                ) : user.phoneNumber ? (
                  <Text type="secondary"><PhoneOutlined style={{ marginRight: 4 }} />{user.phoneNumber}</Text>
                ) : (
                  <Text type="secondary">No email or phone</Text>
                )}
              </Space>
            </Space>

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Email Verified">
                <Space direction="vertical" size={4}>
                  <Space>
                    {user.emailVerified ? <Tag color="green">true</Tag> : <Tag color="orange">false</Tag>}
                  </Space>
                  {!user.emailVerified && user.email && (
                    <Space direction="vertical" size={8}>
                      <Space>
                        <Button
                          type="primary"
                          size="small"
                          loading={resending}
                          onClick={resendVerificationEmail}
                        >
                          Resend verification email
                        </Button>
                        {resendSuccess && <Text type="success">Verification email sent.</Text>}
                        {resendError && <Text type="danger">{resendError}</Text>}
                      </Space>
                      <Space>
                        <Button
                          size="small"
                          danger
                          loading={saving}
                          onClick={() => updateEmailVerified(true)}
                        >
                          Allow Login (override)
                        </Button>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Force-sets emailVerified — use only if email delivery is failing
                        </Text>
                      </Space>
                    </Space>
                  )}
                </Space>
              </Descriptions.Item>
              {(user.verificationEmailSentAt || user.verificationEmailSentCount > 0) && (
                <Descriptions.Item label="Verification email sent">
                  <Space direction="vertical" size={0}>
                    {user.verificationEmailSentAt && (
                      <Text>
                        {moment(user.verificationEmailSentAt.toMillis ? user.verificationEmailSentAt.toMillis() : user.verificationEmailSentAt).format('YYYY-MM-DD HH:mm:ss')}
                      </Text>
                    )}
                    {(user.verificationEmailSentCount || 0) > 0 && (
                      <Text type="secondary">Count: {user.verificationEmailSentCount}</Text>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
              {user.phoneNumber && (
                <Descriptions.Item label="Phone">
                  <Space size={6}>
                    <PhoneOutlined />
                    <span>{user.phoneNumber}</span>
                  </Space>
                </Descriptions.Item>
              )}
              {user.email && (
                <Descriptions.Item label="Email">
                  <Space size={6}>
                    <span>{user.email}</span>
                    {user.emailVerified
                      ? <CheckCircleOutlined style={{ color: '#52c41a' }} title="Verified" />
                      : <Text type="warning" style={{ fontSize: 12 }}>unverified</Text>}
                  </Space>
                </Descriptions.Item>
              )}
              {customer && (
                <Descriptions.Item label="Customer">
                  <a href={`${URLPath}/admin/customers/${customer.id}`}>
                    <code style={{ fontSize: 11 }}>{customer.id}</code>
                  </a>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Login Methods">
                <Space direction="vertical" size={2}>
                  {getAllAuthProviders(user, linkedClient).map((p) => {
                    const norm = p.toLowerCase();
                    let detail = null;
                    if (norm === 'phone') detail = user.phoneNumber || null;
                    else if (norm === 'google') detail = user.googleEmail || user.email || null;
                    else if (norm === 'apple') detail = user.appleEmail || user.email || null;
                    else if (norm === 'email') detail = user.email || null;
                    return (
                      <Space key={p} size={6}>
                        <Tag>{formatLoginMethod(p)}</Tag>
                        {detail && <Text type="secondary" style={{ fontSize: 12 }}>{detail}</Text>}
                      </Space>
                    );
                  })}
                  {getAllAuthProviders(user, linkedClient).length === 0 && '-'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Platform">
                {user.platform || (linkedClient && linkedClient.platform) || 'web'}
              </Descriptions.Item>
              <Descriptions.Item label="Is Anonymous">
                {user.isAnonymous ? 'true' : 'false'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {user.status || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Roles">
                {Array.isArray(user.roles) && user.roles.length > 0
                  ? user.roles.map((role) => (
                    <Tag key={role} color={role === 'client' ? 'blue' : role === 'customer' ? 'cyan' : 'default'}>
                      {role}
                    </Tag>
                  ))
                  : <Text type="secondary">none</Text>
                }
              </Descriptions.Item>
              <Descriptions.Item label="Admin Access">
                <Space direction="vertical" size={4}>
                  <Space>
                    {isStrikeAdmin ? (
                      <Tag icon={<SafetyOutlined />} color="red">Strike Admin</Tag>
                    ) : (
                      <Tag color="default">Not an admin</Tag>
                    )}
                  </Space>
                  <Space>
                    {isStrikeAdmin ? (
                      <Popconfirm
                        title="Remove admin access?"
                        description={`This will remove ${user.displayName || userId} from Strike admins.`}
                        okText="Remove"
                        okType="danger"
                        cancelText="Cancel"
                        onConfirm={() => toggleAdmin(false)}
                      >
                        <Button size="small" danger loading={savingAdmin}>
                          Remove Admin
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Popconfirm
                        title="Grant admin access?"
                        description={`This will give ${user.displayName || userId} full Strike admin access.`}
                        okText="Make Admin"
                        cancelText="Cancel"
                        onConfirm={() => toggleAdmin(true)}
                      >
                        <Button size="small" icon={<SafetyOutlined />} loading={savingAdmin}>
                          Make Admin
                        </Button>
                      </Popconfirm>
                    )}
                    {adminError && <Text type="danger" style={{ fontSize: 12 }}>{adminError}</Text>}
                  </Space>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Human Verified">
                <Space>
                  {user.isHuman ? (
                    <Tooltip title="Verified human (passed reCAPTCHA or admin override)">
                      <Tag icon={<SmileOutlined />} color="green">Human</Tag>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Not yet verified — reCAPTCHA has not passed">
                      <Tag icon={<RobotOutlined />} color="volcano">Unverified</Tag>
                    </Tooltip>
                  )}
                  {!user.isHuman && (
                    <Button
                      size="small"
                      danger
                      loading={savingHuman}
                      onClick={() => adminSetHuman(true)}
                    >
                      <SmileOutlined /> Make Human (override)
                    </Button>
                  )}
                  {user.isHumanOverriddenBy && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Overridden by admin
                    </Text>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="UID">
                {user.uid || user.id}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {user.createdAt && user.createdAt.toMillis ? moment(user.createdAt.toMillis()).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {user.updatedAt && user.updatedAt.toMillis ? moment(user.updatedAt.toMillis()).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Signup Reason">
                {originContext.reason || '-'}
              </Descriptions.Item>
              {originReason === 'claim_tag' && (
                <Descriptions.Item label="Template">
                  {tagLoading ? '…' : (templateTagId || '-')}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Signup Link">
                {originPathLink ? (
                  <a href={originPathLink} target="_blank" rel="noopener noreferrer">{originPathLink}</a>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>

        <Card title="Linked Client" style={{ marginTop: 12 }}>
          {loadingClient ? (
            <CenteredSpinner loading={true} noLayout={false} />
          ) : linkedClient ? (
            <Space direction="vertical">
              <Space>
                <Avatar src={linkedClient.avatar || null}>
                  {(linkedClient.displayName || linkedClient.id || 'C').charAt(0)}
                </Avatar>
                <Text strong>{linkedClient.displayName || linkedClient.id}</Text>
                <Tag color={linkedClient.status === 'live' ? 'green' : 'orange'}>
                  {linkedClient.status || 'unknown'}
                </Tag>
                {linkedByOwnerOnly && (
                  <Tooltip title="Derived from the userId: this client's ownerId matches the user, but the users doc has no clientId stamp (pre-attribution account). Restore stamps it; otherwise the deliberate backfill will.">
                    <Tag icon={<BranchesOutlined />} color="blue" data-testid="derived-client-link">derived</Tag>
                  </Tooltip>
                )}
                {!(Array.isArray(user.roles) && user.roles.includes('client')) && (
                  <Tooltip title={'Pre-intent-gate doc: signed up before roles were recorded. Harmless while this client exists (runtime still honors the old "empty roles = client" convention); the roles+clientId backfill will make it explicit.'}>
                    <Tag color="default" data-testid="pre-intent-gate-note">roles not backfilled</Tag>
                  </Tooltip>
                )}
              </Space>
              <a href={`${URLPath}/admin/clients/${linkedClient.id}`}>Open Client Page</a>
              <Text type="secondary">
                Client Created At: {linkedClient.createdAt && linkedClient.createdAt.toMillis
                  ? moment(linkedClient.createdAt.toMillis()).format('YYYY-MM-DD HH:mm:ss')
                  : '-'}
              </Text>
              {/* Server-observed IP recorded at terms acceptance (write-once; captured
                  for ALL clients, not just Stripe Connect). Fraud correlation: shared
                  IPs across clients link identities that emails/providers hide.
                  The field CAN legitimately be empty — the recording call is
                  fire-and-forget with a short timeout — so undefined, '', and
                  whitespace all render the fallback, and the timestamp only renders
                  when there is an IP to anchor it. Terms-accepted-with-no-IP is also
                  flagged by findUserInconsistencies (compliance evidence gap). */}
              {(() => {
                const termsIP = `${linkedClient.agreedTermsIP || ''}`.trim();
                const termsIPAt = termsIP && linkedClient.agreedTermsIPAt && linkedClient.agreedTermsIPAt.toMillis
                  ? ` at ${moment(linkedClient.agreedTermsIPAt.toMillis()).format('YYYY-MM-DD HH:mm:ss')}`
                  : '';
                return (
                  <Text type="secondary">
                    Agreed Terms IP: {termsIP ? <Text code copyable>{termsIP}</Text> : '— not recorded'}
                    {termsIPAt}
                  </Text>
                );
              })()}
            </Space>
          ) : (
            <Space direction="vertical">
              <Text type="secondary">No linked client found.</Text>
              {user.clientId && (
                <Button href={`${URLPath}/admin/clients/${user.clientId}`} type="link">
                  Try opening linked clientId: {user.clientId}
                </Button>
              )}
            </Space>
          )}
        </Card>
      </div>
    </div>
  );
}
